import { z } from 'zod'
import { oc } from '@orpc/contract'

export const visitCallSignatureResponseSchema = z.object({
  // Zoom Video SDK JWT for this encounter's own session, as a participant. Present only when the
  // visit may be joined right now.
  signature: z.string().optional(),
  // Why there is no signature: the same message the call-access response gives.
  message: z.string().optional(),
})
export type VisitCallSignatureResponse = z.infer<typeof visitCallSignatureResponseSchema>

/**
 * An encounter id that can only name an encounter: any FHIR id Medplum accepts (its own `id` rule,
 * UUID or not), except one made only of dots. The id is joined into the URL the server reads, so a
 * '/' (another path) or a '.' / '..' segment (another FHIR URL) would make it read something else.
 * Shared with AppointmentService.resolveCallAccess, which applies it before any read.
 */
export const ENCOUNTER_ID_PATTERN = /^(?!\.+$)[A-Za-z0-9\-.]{1,64}$/

// The service's read decides whether the encounter exists.
export const visitCallSignatureInputSchema = z.object({
  encounterId: z.string().regex(ENCOUNTER_ID_PATTERN),
})

/**
 * The patient's or guest's Zoom join signature (PD-1705). Guest-capable: no bearer is required, so
 * a patient whose magic link expired can still join an open visit; a bearer, when sent, is checked
 * against the encounter. Signed only when the call-access decision says the visit may be joined.
 * Providers use `visitVideoToken` (mobile) and the Medplum overlay (web) instead.
 */
export const visitCallSignature = oc
  .route({ method: 'GET', path: '/v2/visits/{encounterId}/call-signature' })
  .input(visitCallSignatureInputSchema)
  .output(visitCallSignatureResponseSchema)
